<script setup lang="ts">
import { computed } from 'vue';
import { extractRatingFromPerformances, type Stage, type StudyUnit } from '@/types/CourseContent';
import {
    getDisplayTime,
    capElementTimes,
    type StudentRatingPerformance,
    type StudentRatingTiming,
    type StudyUnitStudyQuizPerformance,
    type PersonalInformationRow,
} from '@/types/Performance';
import { formatDate } from '@/functions/formatDate';
import StudyQuestionsTableCell from './PerformanceTable/StudyQuestionsTableCell.vue';
import QuizTestTableCell from './PerformanceTable/QuizTestTableCell.vue';
import TestBottomTableRow from './PerformanceTable/TestBottomTableRow.vue';

const maxElementTime = import.meta.env.VITE_MAX_ELEMENT_TIME;
const componentProps = defineProps<{
    studentPerformance: StudentRatingPerformance|null,
    studentTiming: StudentRatingTiming|null,
    studentStudyQuizzes: StudyUnitStudyQuizPerformance[],
    loading: boolean,
}>();

type PersonalInformationTable = {
    finalTestName: string|undefined,
    finalTestData: PersonalInformationRow|undefined,
    rows: PersonalInformationRow[],
};

const cols = [
    {
        name: 'unit', label: 'Study Unit', field: 'unitLabel', align: 'left' as const,
    },
    {
        name: 'outlineTime', label: 'Study Guide Time', field: 'outlineTime', align: 'left' as const,
    },
    {
        name: 'studyQuestionsTime', label: 'Study Questions Time', field: 'studyQuestionsTime', align: 'left' as const,
    },
    {
        name: 'studyQuestionsScore', label: 'Study Questions Score', field: 'studyQuestionsScore', align: 'left' as const,
    },
    {
        name: 'finalQuizTime', label: 'Final Quiz Time', field: 'finalQuizTime', align: 'left' as const,
    },
    {
        name: 'finalQuizScore', label: 'Final Quiz Score', field: 'finalQuizScore', align: 'left' as const,
    },
    {
        name: 'optionalQuizTime', label: 'Optional Quiz Time', field: 'optionalQuizTime', align: 'left' as const,
    },
    {
        name: 'optionalQuizScore', label: 'Optional Quiz Score', field: 'optionalQuizScore', align: 'left' as const,
    },
    {
        name: 'dateCompleted', label: 'Completion Date', field: 'dateCompleted', align: 'left' as const,
    },
    {
        name: 'totalTime', label: 'Total Time', field: 'totalTime', align: 'left' as const,
    },
];

const finalExamsCols = [
    {
        name: 'unit', label: 'Exam Name', field: 'unitLabel', align: 'left' as const,
    },
    {
        name: 'finalQuizScore', label: 'Final Score', field: 'finalQuizScore', align: 'left' as const,
    },
    {
        name: 'dateCompleted', label: 'Completion Date', field: 'dateCompleted', align: 'left' as const,
    },
    {
        name: 'totalTime', label: 'Total Time', field: 'totalTime', align: 'left' as const,
    },
];

const tables = computed<PersonalInformationTable[]>(() => {
    const computedTables: PersonalInformationTable[] = [];
    const [studentPerformance, studentTiming, studentStudyQuizzes] = [
        componentProps.studentPerformance,
        componentProps.studentTiming === null ? null : capElementTimes(componentProps.studentTiming, maxElementTime),
        componentProps.studentStudyQuizzes,
    ];

    if (
        studentPerformance !== null
        && studentTiming !== null
        && studentStudyQuizzes.length > 0
    ) {
        const rating = extractRatingFromPerformances(studentPerformance);

        rating.stages.forEach((stage: Stage) => {
            const stageRows: PersonalInformationRow[] = [];
            stage.studyUnits.forEach((studyUnit: StudyUnit) => {
                const studyPerformance = studentPerformance.studyUnitPerformance.find(
                    (perf) => perf.stageNumber === stage.stageNumber
                        && perf.studyUnitNumber === studyUnit.studyUnitNumber,
                );

                const studyTimes = studentTiming.getStudyUnitTime(studyUnit.studyUnitNumber);

                const studyQuiz = studentStudyQuizzes.find(
                    (quiz) => quiz.studyUnitNumber === studyUnit.studyUnitNumber,
                );

                if (studyPerformance === undefined || studyTimes === undefined) {
                    // Need to push numbered, empty row
                    return;
                }

                stageRows.push({
                    unitLabel: studyPerformance.studyUnitNumber.toString(),
                    outlineTime: getDisplayTime(studyTimes.outlineTime),
                    studyQuestionsTime: getDisplayTime(studyTimes.studyQuestionsTime),
                    studyQuestionsScore: studyQuiz ?? '--',
                    finalQuizTime: getDisplayTime(studyTimes.finalQuizTime),
                    finalQuizScore: studyPerformance.finalQuizPerformance ?? '--',
                    optionalQuizTime: getDisplayTime(studyTimes.optionalQuizTime),
                    optionalQuizScore: studyPerformance.optionalQuizPerformance ?? '--',
                    dateCompleted: formatDate(studyPerformance.finalQuizPerformance.completedDate),
                    totalTime: getDisplayTime(
                        studentTiming.getStudyUnitTotalTime(studyUnit.studyUnitNumber),
                    ),
                });
            });

            const stagePerformance = studentPerformance.stagePerformance.find(
                (perf) => perf.stageNumber === stage.stageNumber,
            );

            if (stagePerformance === undefined) {
                // Need to push numbered, empty row
                computedTables.push({
                    finalTestName: undefined,
                    finalTestData: undefined,
                    rows: stageRows,
                });
                return;
            }
            computedTables.push({
                finalTestName: `Stage # ${stage.stageNumber}`,
                finalTestData: {
                    unitLabel: `Stage Test #${stagePerformance.stageNumber}`,
                    outlineTime: '--',
                    studyQuestionsTime: '--',
                    studyQuestionsScore: '--',
                    finalQuizTime: '--',
                    finalQuizScore: stagePerformance ?? '--',
                    optionalQuizTime: '--',
                    optionalQuizScore: '--',
                    dateCompleted: formatDate(stagePerformance.completedDate),
                    totalTime: getDisplayTime(studentTiming.getStageTestTime(stage.stageNumber)),
                },
                rows: stageRows,
            });
        });

        const finalCourseTestRows: PersonalInformationRow[] = [];
        finalCourseTestRows.push({
            unitLabel: 'End-Of-Course Test',
            outlineTime: '--',
            studyQuestionsTime: '--',
            studyQuestionsScore: '--',
            finalQuizTime: '--',
            finalQuizScore: studentPerformance.eocPerformance ?? '--',
            optionalQuizTime: '--',
            optionalQuizScore: '--',
            dateCompleted: formatDate(studentPerformance.eocPerformance.completedDate),
            totalTime: getDisplayTime(studentTiming.eocTime),
        });

        let bonusQuizNumber = 0;
        for (let practiceNumber = 0; practiceNumber < 5; practiceNumber += 1) {
            bonusQuizNumber += practiceNumber > 1 ? 1 : 0;

            /**
             * Determines test label based on Test Number:
             * Tests 1-2: "Practice Test"
             * Tests 3+: "Bonus Test"
             */
            const label = practiceNumber <= 1
                ? `Practice Test #${practiceNumber + 1}`
                : `Bonus Test #${bonusQuizNumber}`;

            const practicePerformance = studentPerformance.practicePerformance[practiceNumber]
                ?? null;
            const practiceTime = studentTiming.practiceTimes[practiceNumber] ?? null;

            finalCourseTestRows.push({
                unitLabel: label,
                outlineTime: '--',
                studyQuestionsTime: '--',
                studyQuestionsScore: '--',
                finalQuizTime: '--',
                finalQuizScore: practicePerformance ?? '--',
                optionalQuizTime: '--',
                optionalQuizScore: '--',
                dateCompleted: practicePerformance
                    ? formatDate(practicePerformance.completedDate)
                    : '--',
                totalTime: practiceTime ? getDisplayTime(practiceTime.practiceTime) : '--',
            });
        }

        computedTables.push({
            finalTestName: 'Final Course Tests',
            finalTestData: undefined,
            rows: finalCourseTestRows,
        });
    }

    return computedTables;
});
</script>
<template>
    <div
        id="student-personal-information"
        class="col-12"
    >
        <q-table
            v-for="table in tables"
            :key="table.rows[0].unitLabel"
            :title="table.finalTestName"
            :columns="table.finalTestName === 'Final Course Tests' ? finalExamsCols : cols"
            :rows="table.rows"
            :rows-per-page-options="[0]"
            :hide-pagination="true"
            :loading="loading"
            :no-data-label="loading ? 'Loading...' : 'No data'"
            title-class="text-fsum-all-blue text-bold title-row"
            table-header-class="bg-fsum-all-blue text-fsum-all-blue text-bold header-row"
            class="q-mb-xl"
            flat
        >
            <template #body-cell-studyQuestionsScore="studyQuestionsProps">
                <StudyQuestionsTableCell
                    :study-questions="studyQuestionsProps.value"
                />
            </template>

            <template #body-cell-finalQuizScore="quizTestProps">
                <QuizTestTableCell
                    :quiz-test="quizTestProps.value"
                />
            </template>

            <template #body-cell-optionalQuizScore="optionalQuizProps">
                <QuizTestTableCell
                    :quiz-test="optionalQuizProps.value"
                />
            </template>

            <template #bottom-row>
                <TestBottomTableRow
                    v-if="table.finalTestData"
                    :test-name="table.finalTestName"
                    :test-row="table.finalTestData"
                />
                <tr
                    v-else
                    class="bottom-row"
                >
                    <td colspan="100" />
                </tr>
            </template>
        </q-table>
    </div>
</template>
<style lang="scss" scoped>
#student-personal-information {
    background: none;
}
:deep(.q-table__top) {
    background: var(--fsum-light-blue);
}
:deep(.title-row) {
    font-size: 24px;
}
.header-row, :deep(td), :deep(.q-btn), :deep(.q-icon) {
    font-size: 12px;
}
:deep(tr.bottom-row td) {
    background: var(--fsum-light-blue);
    color: var(--fsum-dark-blue);
    font-size: 16px;
}
</style>
